/**
 * PII Scanner Hook
 *
 * Uses a Rust-compiled WASM module to detect PII patterns in text input.
 * Provides instant client-side feedback before text reaches the server
 * (where Presidio serves as defense-in-depth).
 *
 * WASM module is lazily loaded on mount. Gracefully degrades if WASM
 * fails to load — the submit flow proceeds normally without scanning.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface PiiFinding {
  entity_type: string;
  start: number;
  end: number;
  text: string;
  confidence: number;
}

export interface PiiScanResult {
  has_pii: boolean;
  findings: PiiFinding[];
  scan_time_ms: number;
}

interface WasmModule {
  default: () => Promise<void>;
  scan_text: (input: string) => PiiScanResult;
  scan_text_fast: (input: string) => boolean;
}

interface UsePiiScannerReturn {
  /** WASM module loaded and ready */
  isSupported: boolean;
  /** Currently loading the WASM module */
  isLoading: boolean;
  /** Scan results (null until first scan) */
  result: PiiScanResult | null;
  /** Quick boolean check from latest scan */
  hasPii: boolean;
  /** Trigger a debounced scan with the given input */
  scan: (input: string) => void;
  /** Clear scan results */
  clear: () => void;
  /** Error message */
  error: string | null;
}

const DEBOUNCE_MS = 300;

export function usePiiScanner(): UsePiiScannerReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [isSupported, setIsSupported] = useState(false);
  const [result, setResult] = useState<PiiScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const wasmRef = useRef<WasmModule | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingInputRef = useRef<string | null>(null);

  // Lazy-load WASM module on mount
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const wasm = await import('crucio-pii-wasm');
        await wasm.default();
        if (!cancelled) {
          const module = wasm as unknown as WasmModule;
          wasmRef.current = module;
          setIsSupported(true);
          setIsLoading(false);

          // Replay any scan attempted before WASM was ready
          if (pendingInputRef.current) {
            try {
              const scanResult = module.scan_text(pendingInputRef.current);
              setResult(scanResult);
            } catch { /* graceful degradation */ }
            pendingInputRef.current = null;
          }
        }
      } catch {
        if (!cancelled) {
          setError('PII scanner unavailable');
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const scan = useCallback((input: string) => {
    // Clear pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // WASM not loaded yet — store for replay after init
    if (!wasmRef.current) {
      pendingInputRef.current = input.trim() ? input : null;
      return;
    }

    if (!input.trim()) {
      setResult(null);
      return;
    }

    // Clear stale result while debounce is pending
    setResult(null);

    debounceRef.current = setTimeout(() => {
      try {
        const scanResult = wasmRef.current!.scan_text(input);
        setResult(scanResult);
        setError(null);
      } catch {
        setError('PII scan failed');
        setResult(null);
      }
    }, DEBOUNCE_MS);
  }, []);

  const clear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setResult(null);
    setError(null);
  }, []);

  return {
    isSupported,
    isLoading,
    result,
    hasPii: result?.has_pii ?? false,
    scan,
    clear,
    error,
  };
}
